// src/utils/imageProcess.js
// Utilitários para processamento de imagens client-side: crop central quadrado + compressão WebP.

/**
 * Recebe uma dataURL/Blob/HTMLImageElement e retorna uma dataURL processada:
 *  - Recorte quadrado central
 *  - Redimensionamento para `size`×`size`
 *  - Compressão para WebP (com fallback JPEG se WebP não suportado)
 *
 * @param {string|File|HTMLImageElement} input
 * @param {object} opts
 * @param {number} opts.size  Lado do quadrado final (px). Default 400.
 * @param {number} opts.quality Qualidade 0..1. Default 0.82.
 * @param {"webp"|"jpeg"} opts.preferType Default "webp".
 * @returns {Promise<string>} dataURL processada.
 */
export async function processarImagemAvatar(input, opts = {}) {
  const { size = 400, quality = 0.82, preferType = "webp" } = opts;
  const img = await carregarImagem(input);

  // Recorte central
  const min = Math.min(img.naturalWidth, img.naturalHeight);
  const sx = Math.floor((img.naturalWidth  - min) / 2);
  const sy = Math.floor((img.naturalHeight - min) / 2);

  const canvas = document.createElement("canvas");
  canvas.width  = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);

  // Tenta WebP; se navegador não suportar, cai para JPEG
  const tipoFinal = preferType === "webp" ? "image/webp" : "image/jpeg";
  const dataUrl   = canvas.toDataURL(tipoFinal, quality);

  // WebP indisponível => alguns navegadores retornam "data:image/png" silenciosamente
  if (!dataUrl.startsWith(`data:${tipoFinal}`)) {
    return canvas.toDataURL("image/jpeg", quality);
  }
  return dataUrl;
}

/** Carrega input em HTMLImageElement, independente do tipo (string dataURL, File, ou Image). */
function carregarImagem(input) {
  return new Promise((resolve, reject) => {
    if (input instanceof HTMLImageElement) {
      if (input.complete) resolve(input);
      else {
        input.onload = () => resolve(input);
        input.onerror = () => reject(new Error("Imagem inválida."));
      }
      return;
    }
    if (typeof input === "string") {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Imagem inválida."));
      img.src = input;
      return;
    }
    if (input instanceof Blob || input instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Imagem inválida."));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error("Não foi possível ler o arquivo."));
      reader.readAsDataURL(input);
      return;
    }
    reject(new Error("Tipo de input não suportado."));
  });
}

/** Retorna o tamanho aproximado em bytes de uma dataURL base64. */
export function tamanhoBase64Bytes(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return 0;
  const i = dataUrl.indexOf(",");
  const b64 = i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
  return Math.floor((b64.length * 3) / 4);
}
